import { normaliseKey, toBoolean, toNumber } from './columns.ts';

/**
 * The columns the *results* importer understands, and how each one is read.
 *
 * The same contract `columns.ts` is for tournaments: a template's field keys are free text, so
 * this is the vocabulary that gives them meaning, matched on a normalised spelling. A key the
 * importer doesn't recognise is reported and ignored rather than guessed at.
 *
 * Deliberately a separate list rather than an extension of the tournament one. The two files
 * describe different things and a shared vocabulary would have to make every column optional to
 * accommodate both, which is exactly how a missing required column stops being catchable.
 *
 * Whether a cell *had* to say something is not decided here — a first name is needed only for a
 * card the room does not already hold, and that is a question about the file as a whole.
 * `resultPlan.ts` owns it.
 */

/** One row of a results file, read. Null means the cell was blank or the column absent. */
export interface ResultRowDraft {
  cardId: string | null;
  place: number | null;
  firstName: string | null;
  lastName: string | null;
  city: string | null;
  state: string | null;
  prize: number | null;
  prizeNotes: string | null;
  isInDeal: boolean | null;
  buyins: number | null;
  /**
   * The room's contact for this card. Reaches a card the room does not already hold — a matched
   * card keeps whatever is on file, because a stale spreadsheet must not be able to redirect
   * somebody's verification code. See `resultPlan.ts`.
   */
  email: string | null;
  phone: string | null;
}

export const emptyResultRow = (): ResultRowDraft => ({
  cardId: null,
  place: null,
  firstName: null,
  lastName: null,
  city: null,
  state: null,
  prize: null,
  prizeNotes: null,
  isInDeal: null,
  buyins: null,
  email: null,
  phone: null,
});

export interface ResultColumn {
  key: string;
  label: string;
  /**
   * Required to record an entry at all. Checked against the template before a file is chosen —
   * a file that cannot say who played has nothing to import.
   *
   * Note `place` is *not* one of these. A results sheet is the whole field, and most of a large
   * one is people who entered without finishing in the money; the column is still needed, but a
   * blank cell in it is ordinary rather than missing.
   */
  required: boolean;
  example: string;
  notes?: string;
  aliases: readonly string[];
  /** Reads one cell into the draft. Returns why it couldn't, or null. Blank is never an error. */
  read: (raw: string, draft: ResultRowDraft) => string | null;
}

export const RESULT_COLUMNS: readonly ResultColumn[] = [
  {
    key: 'card_id',
    label: 'Card ID',
    required: true,
    example: '4332893257',
    notes:
      "The player's card number at your room. A card you don't hold becomes a new player. On a " +
      "placed row, a blank card, the template's marker, or a card you don't hold with no name is " +
      'an unclaimed place — a finish nobody has claimed yet.',
    aliases: ['id', 'cardid', 'playerid', 'playercard', 'player'],
    read: (raw, draft) => {
      draft.cardId = raw.trim() || null;
      return null;
    },
  },
  {
    key: 'place',
    label: 'Place',
    required: false,
    example: '1',
    notes:
      'Finishing position. Blank for somebody who entered and did not reach a paid place — the ' +
      'places that are filled in must run 1, 2, 3… with no gaps and no repeats.',
    aliases: ['position', 'finish', 'rank', 'pos'],
    read: (raw, draft) => {
      if (raw.trim() === '') return null;
      const value = toNumber(raw);
      if (value === null || !Number.isInteger(value)) return `"${raw.trim()}" isn't a place.`;
      if (value < 1) return 'Places start at 1.';
      draft.place = value;
      return null;
    },
  },
  {
    key: 'first_name',
    label: 'First name',
    required: false,
    example: 'Steven',
    notes: 'Only needed for a card your room does not already hold.',
    aliases: ['firstname', 'first', 'givenname'],
    read: (raw, draft) => {
      draft.firstName = raw.trim() || null;
      return null;
    },
  },
  {
    key: 'last_name',
    label: 'Last name',
    required: false,
    example: 'McKoy',
    notes: 'Only needed for a card your room does not already hold.',
    aliases: ['lastname', 'last', 'surname', 'familyname'],
    read: (raw, draft) => {
      draft.lastName = raw.trim() || null;
      return null;
    },
  },
  {
    key: 'city',
    label: 'City',
    required: false,
    example: 'Coral Springs',
    aliases: ['town'],
    read: (raw, draft) => {
      draft.city = raw.trim() || null;
      return null;
    },
  },
  {
    key: 'state',
    label: 'State',
    required: false,
    example: 'FL',
    notes: 'Free text — a province or region is fine.',
    aliases: ['province', 'region', 'st'],
    read: (raw, draft) => {
      draft.state = raw.trim() || null;
      return null;
    },
  },
  {
    key: 'prize',
    label: 'Prize won',
    required: true,
    example: '29578',
    notes: 'Whole dollars. 0 for a place that paid nothing.',
    aliases: ['prizewon', 'winnings', 'payout', 'amount', 'cash'],
    read: (raw, draft) => {
      if (raw.trim() === '') return null;
      const value = toNumber(raw);
      if (value === null) return `"${raw.trim()}" isn't an amount.`;
      if (!Number.isInteger(value)) return 'Prizes are whole dollars.';
      if (value < 0) return "A prize can't be negative.";
      draft.prize = value;
      return null;
    },
  },
  {
    key: 'prize_notes',
    label: 'Prize notes',
    required: false,
    example: 'Three-handed deal; includes a $5,300 seat',
    notes: 'Anything the figure alone does not say.',
    aliases: ['prizenote', 'notes', 'note', 'comment'],
    read: (raw, draft) => {
      draft.prizeNotes = raw.trim() || null;
      return null;
    },
  },
  {
    key: 'in_deal',
    label: 'In a deal',
    required: false,
    example: 'TRUE',
    notes: 'TRUE/FALSE, yes/no or 1/0. Blank counts as no.',
    aliases: ['indeal', 'deal', 'chop', 'chopped', 'dealmade'],
    read: (raw, draft) => {
      if (raw.trim() === '') return null;
      const value = toBoolean(raw);
      if (value === null) return `"${raw.trim()}" isn't a yes or a no.`;
      draft.isInDeal = value;
      return null;
    },
  },
  {
    key: 'buyins',
    label: 'Buy-ins',
    required: false,
    example: '2',
    notes: 'Entries bought, initial plus re-entries. Blank means you do not track it.',
    aliases: ['buyin', 'entries', 'entrycount', 'rebuys'],
    read: (raw, draft) => {
      if (raw.trim() === '') return null;
      const value = toNumber(raw);
      if (value === null || !Number.isInteger(value)) return `"${raw.trim()}" isn't a count.`;
      if (value < 1) return 'Buy-ins start at 1. Leave it blank if you do not track it.';
      draft.buyins = value;
      return null;
    },
  },
  {
    key: 'email',
    label: 'Email',
    required: false,
    example: 'ariel.rosello@example.com',
    notes:
      'How a player proves this card is theirs. Only used for a card your room does not ' +
      'already hold — an import never overwrites a contact already on file.',
    aliases: ['emailaddress', 'mail'],
    read: (raw, draft) => {
      const value = raw.trim();
      if (value === '') return null;
      // Deliberately looser than the server's rule. This is feedback, not the control — the same
      // relationship `validate.ts` has to the points-system validator — and the mistake worth
      // catching here is the one a whole column of a spreadsheet makes at once. A client rule
      // that tried to match the server exactly would refuse addresses the API accepts the moment
      // the two drifted, which is the worse of the two failures.
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) return `"${value}" isn't an email address.`;
      draft.email = value;
      return null;
    },
  },
  {
    key: 'phone',
    label: 'Phone',
    required: false,
    example: '(954) 555-0142',
    notes:
      'Stored in international form. A number with no country code is read as belonging to ' +
      'the default region this platform is configured for.',
    aliases: ['phonenumber', 'mobile', 'cell', 'telephone', 'tel'],
    read: (raw, draft) => {
      const value = raw.trim();
      if (value === '') return null;
      // Only "does this contain a number at all". Working out which digits are a country code is
      // the server's job and is done in exactly one place, because a claim has to be able to find
      // the row this writes — see ContactNormalizer.
      if (!/\d/.test(value)) return `"${value}" isn't a phone number.`;
      draft.phone = value;
      return null;
    },
  },
];

const BY_SPELLING = new Map<string, ResultColumn>();
for (const column of RESULT_COLUMNS) {
  BY_SPELLING.set(normaliseKey(column.key), column);
  for (const alias of column.aliases) BY_SPELLING.set(normaliseKey(alias), column);
}

/** The column a template field key means, or null where the importer has no use for it. */
export const resultColumnFor = (fieldKey: string): ResultColumn | null =>
  BY_SPELLING.get(normaliseKey(fieldKey)) ?? null;

export const REQUIRED_RESULT_COLUMNS = RESULT_COLUMNS.filter((c) => c.required);

/** Field keys naming the two columns a new player cannot be created without. */
export const NAME_KEYS = ['first_name', 'last_name'] as const;

/**
 * The required columns a room's chosen field keys don't cover. Empty means the template can
 * record a finish — which is what decides whether it is offered on the first step at all.
 */
export function missingRequiredResultColumns(fieldKeys: readonly string[]): ResultColumn[] {
  const covered = new Set(
    fieldKeys
      .map((key) => resultColumnFor(key)?.key)
      .filter((key): key is string => key !== undefined),
  );
  return REQUIRED_RESULT_COLUMNS.filter((column) => !covered.has(column.key));
}

/** Whether the template can describe somebody the room has never carded. */
export function canCreatePlayers(fieldKeys: readonly string[]): boolean {
  const covered = new Set(
    fieldKeys
      .map((key) => resultColumnFor(key)?.key)
      .filter((key): key is string => key !== undefined),
  );
  return NAME_KEYS.every((key) => covered.has(key));
}
